import React from 'react';
import * as Icons from 'lucide-react';
import { DOMAINS, getAllDomains } from '../../config/domains';
import { ABLE_COLORS } from '../../constants';

/**
 * DomainSettings - Modal to manage which domains appear in sidebar
 */
export default function DomainSettings({
  enabledDomains,
  onEnableDomain,
  onDisableDomain,
  onReorderDomains,
  onClose,
}) {
  const allDomains = getAllDomains();
  
  // Split into enabled and available
  const enabled = enabledDomains
    .map(id => DOMAINS[id])
    .filter(Boolean);
  
  const available = allDomains
    .filter(domain => !enabledDomains.includes(domain.id));

  // Get icon component
  const getIcon = (iconName) => {
    return Icons[iconName] || Icons.Circle;
  };

  // Move domain up or down
  const moveDomain = (domainId, direction) => {
    const index = enabledDomains.indexOf(domainId);
    if (index === -1) return;

    const newIndex = direction === 'up' ? index - 1 : index + 1;
    if (newIndex < 0 || newIndex >= enabledDomains.length) return;

    const newOrder = [...enabledDomains];
    [newOrder[index], newOrder[newIndex]] = [newOrder[newIndex], newOrder[index]];
    onReorderDomains(newOrder);
  };

  return (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      background: 'rgba(0,0,0,0.4)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 1000,
      padding: '20px',
    }}
    onClick={(e) => {
      if (e.target === e.currentTarget) onClose();
    }}
    >
      <div style={{
        background: 'white',
        borderRadius: '16px',
        width: '100%',
        maxWidth: '400px',
        maxHeight: '80vh',
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
        boxShadow: '0 20px 60px rgba(0,0,0,0.2)',
      }}>
        {/* Header */}
        <div style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          padding: '16px 20px',
          borderBottom: `1px solid ${ABLE_COLORS.border}`,
        }}>
          <div>
            <h2 style={{
              margin: 0,
              fontSize: '18px',
              fontWeight: 600,
              color: ABLE_COLORS.textPrimary,
            }}>
              Manage Domains
            </h2>
            <p style={{
              margin: '4px 0 0',
              fontSize: '13px',
              color: ABLE_COLORS.textMuted,
            }}>
              Add or remove areas of your life
            </p>
          </div>
          <button
            onClick={onClose}
            style={{
              background: 'none',
              border: 'none',
              padding: '8px',
              cursor: 'pointer',
              borderRadius: '8px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <Icons.X size={20} color={ABLE_COLORS.textMuted} />
          </button>
        </div>

        {/* Content */}
        <div style={{
          flex: 1,
          overflowY: 'auto',
          padding: '16px 20px',
        }}>
          {/* Active domains */}
          <div style={{ marginBottom: '24px' }}>
            <h3 style={{
              margin: '0 0 12px',
              fontSize: '12px',
              fontWeight: 600,
              color: ABLE_COLORS.textMuted,
              textTransform: 'uppercase',
              letterSpacing: '0.5px',
            }}>
              Active
            </h3>
            
            {enabled.map((domain, index) => {
              const Icon = getIcon(domain.icon);
              const canRemove = domain.canDisable && enabledDomains.length > 1;
              
              return (
                <div
                  key={domain.id}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '12px',
                    padding: '10px 12px',
                    background: '#F9F9F8',
                    borderRadius: '8px',
                    marginBottom: '8px',
                  }}
                >
                  {/* Drag handle */}
                  <Icons.GripVertical 
                    size={16} 
                    color={ABLE_COLORS.textMuted}
                    style={{ cursor: 'grab', flexShrink: 0 }}
                  />
                  
                  {/* Icon with color */}
                  <div style={{
                    width: 32,
                    height: 32,
                    borderRadius: '8px',
                    background: `${domain.color}15`,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexShrink: 0,
                  }}>
                    <Icon size={18} color={domain.color} />
                  </div>
                  
                  {/* Name and description */}
                  <div style={{ flex: 1 }}>
                    <span style={{
                      fontSize: '14px',
                      fontWeight: 500,
                      color: ABLE_COLORS.textPrimary,
                    }}>
                      {domain.name}
                    </span>
                    <span style={{
                      display: 'block',
                      fontSize: '12px',
                      color: ABLE_COLORS.textMuted,
                    }}>
                      {domain.description}
                    </span>
                  </div>
                  
                  {/* Move buttons */}
                  <button
                    onClick={() => moveDomain(domain.id, 'up')}
                    disabled={index === 0}
                    style={{
                      background: 'none',
                      border: 'none',
                      padding: '4px',
                      cursor: index === 0 ? 'default' : 'pointer',
                      opacity: index === 0 ? 0.3 : 1,
                      borderRadius: '4px',
                    }}
                  >
                    <Icons.ChevronUp size={16} color={ABLE_COLORS.textMuted} />
                  </button>
                  <button
                    onClick={() => moveDomain(domain.id, 'down')}
                    disabled={index === enabled.length - 1}
                    style={{
                      background: 'none',
                      border: 'none',
                      padding: '4px',
                      cursor: index === enabled.length - 1 ? 'default' : 'pointer',
                      opacity: index === enabled.length - 1 ? 0.3 : 1,
                      borderRadius: '4px',
                    }}
                  >
                    <Icons.ChevronDown size={16} color={ABLE_COLORS.textMuted} />
                  </button>
                  
                  {/* Remove button */}
                  {canRemove ? (
                    <button
                      onClick={() => onDisableDomain(domain.id)}
                      style={{
                        background: 'none',
                        border: 'none',
                        padding: '4px 8px',
                        cursor: 'pointer',
                        fontSize: '12px',
                        fontWeight: 500,
                        color: ABLE_COLORS.textMuted,
                        borderRadius: '4px',
                      }}
                    >
                      Remove
                    </button>
                  ) : (
                    <span style={{
                      fontSize: '11px',
                      color: ABLE_COLORS.textMuted,
                      padding: '4px 8px',
                    }}>
                      Required
                    </span>
                  )}
                </div>
              );
            })}
          </div>

          {/* Available domains */}
          {available.length > 0 && (
            <div>
              <h3 style={{
                margin: '0 0 12px',
                fontSize: '12px',
                fontWeight: 600,
                color: ABLE_COLORS.textMuted,
                textTransform: 'uppercase',
                letterSpacing: '0.5px',
              }}>
                Available
              </h3>
              
              {available.map(domain => {
                const Icon = getIcon(domain.icon);
                return (
                  <div
                    key={domain.id}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '12px',
                      padding: '10px 12px',
                      background: 'transparent',
                      borderRadius: '8px',
                      marginBottom: '8px',
                      border: `1px dashed ${ABLE_COLORS.border}`,
                    }}
                  >
                    {/* Icon with color */}
                    <div style={{
                      width: 32,
                      height: 32,
                      borderRadius: '8px',
                      background: `${domain.color}10`,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      flexShrink: 0,
                    }}>
                      <Icon size={18} color={`${domain.color}80`} />
                    </div>
                    
                    {/* Name and description */}
                    <div style={{ flex: 1 }}>
                      <span style={{
                        fontSize: '14px',
                        fontWeight: 500,
                        color: ABLE_COLORS.textSecondary,
                      }}>
                        {domain.name}
                      </span>
                      <span style={{
                        display: 'block',
                        fontSize: '12px',
                        color: ABLE_COLORS.textMuted,
                      }}>
                        {domain.description}
                      </span>
                    </div>
                    
                    {/* Add button */}
                    <button
                      onClick={() => onEnableDomain(domain.id)}
                      style={{
                        background: ABLE_COLORS.primaryLight,
                        border: 'none',
                        padding: '6px 12px',
                        cursor: 'pointer',
                        fontSize: '12px',
                        fontWeight: 500,
                        color: ABLE_COLORS.primary,
                        borderRadius: '6px',
                      }}
                    >
                      Add
                    </button>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Footer */}
        <div style={{
          padding: '16px 20px',
          borderTop: `1px solid ${ABLE_COLORS.border}`,
          display: 'flex',
          justifyContent: 'flex-end',
        }}>
          <button
            onClick={onClose}
            style={{
              background: ABLE_COLORS.primary,
              border: 'none',
              padding: '10px 20px',
              cursor: 'pointer',
              fontSize: '14px',
              fontWeight: 600,
              color: 'white',
              borderRadius: '8px',
            }}
          >
            Done
          </button>
        </div>
      </div>
    </div>
  );
}
